import { Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import TeacherShell, { TeacherPageHeader } from '@/components/layout/TeacherShell'
import { Card, CardContent, Button } from '@/components/ui'

const BAND_ORDER = ['5k', '6k', '7k', '8k', '9k', '10k', '11k', '12k', '13k', '14k']

export default function TrialResults({ assessment, result, duration, review = [] }) {
  const { t } = useTranslation()
  const bandScores = result?.band_scores || {}
  const testedBands = BAND_ORDER.filter(b => bandScores[b])

  return (
    <TeacherShell scrollTitle={<><Link href={`/teacher/assessments/${assessment.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{assessment.title}</Link>{`  ›  ${t('teacherAssessmentResults.results')}`}</>} maxWidth="narrow">
      {/* Preview banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 mb-6">
        <p className="text-sm text-amber-800 dark:text-amber-300">
          {t('teacherAssessmentResults.previewNote')}
        </p>
      </div>

      <TeacherPageHeader title={t('teacherAssessmentResults.yourResults')} />

      {/* Headline result */}
      <div className="text-center mb-10">
        <p className="text-5xl font-bold text-gray-900 dark:text-white mb-2">
          {result?.estimated_vocab_size?.toLocaleString() || '—'}
        </p>
        <p className="text-lg text-gray-500 dark:text-gray-400">{t('teacherAssessmentResults.estimatedWordFamilies')}</p>

        <div className="mt-4 space-y-1">
          <p className="text-xl text-gray-700 dark:text-gray-200">
            {t('teacherAssessmentResults.readingLevel')} <span className="font-semibold">{result?.estimated_grade_level || '—'}</span>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {result?.grade_description}
          </p>
        </div>

        {duration && (
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">
            {t('teacherAssessmentResults.completedIn')} {duration}
          </p>
        )}
      </div>

      {/* Band breakdown */}
      {testedBands.length > 0 && (
        <Card className="mb-8">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('teacherAssessmentResults.bandBreakdown')}</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 font-medium">{t('teacherAssessments.band')}</th>
                  <th className="pb-2 font-medium text-center">{t('teacherAssessmentResults.score')}</th>
                  <th className="pb-2 font-medium text-right">{t('teacherAssessmentResults.accuracy')}</th>
                </tr>
              </thead>
              <tbody>
                {testedBands.map(band => {
                  const scores = bandScores[band]
                  const pct = scores?.pct ?? 0
                  return (
                    <tr key={band} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 text-gray-700 dark:text-gray-300 font-medium">{band}</td>
                      <td className="py-2 text-center text-gray-600 dark:text-gray-400">
                        {scores?.correct}/{scores?.total}
                      </td>
                      <td className="py-2 text-right">
                        <span className={pct >= 60 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                          {pct}%
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('teacherAssessmentResults.total')}</span>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {result?.total_correct}/{result?.total_items}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question review */}
      {review.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('teacherAssessmentResults.questionReview')}</h2>
          <div className="space-y-4 mb-8">
            {review.map((item, idx) => (
              <Card key={idx}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p
                      className="text-base text-gray-900 dark:text-white leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatPrompt(item.prompt) }}
                    />
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-3 shrink-0">
                      {item.band}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {item.options.map((text, optIdx) => {
                      const isSelected = item.selected_index === optIdx
                      const isCorrect = item.correct_index === optIdx
                      const wasWrong = isSelected && !item.correct

                      let className = 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm '
                      if (isCorrect) {
                        className += 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                      } else if (wasWrong) {
                        className += 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                      } else {
                        className += 'text-gray-500 dark:text-gray-400'
                      }

                      return (
                        <div key={optIdx} className={className}>
                          <span className="text-xs font-medium w-5 shrink-0">
                            {String.fromCharCode(65 + optIdx)}.
                          </span>
                          <span className="flex-1">{text}</span>
                          {isCorrect && (
                            <svg className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {wasWrong && (
                            <svg className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-center">
        <Link href={`/teacher/assessments/${assessment.id}`}>
          <Button variant="secondary">{t('teacherAssessmentResults.backToAssessment')}</Button>
        </Link>
        <Link href="/teacher/assessments">
          <Button variant="secondary">{t('teacherAssessmentResults.allAssessments')}</Button>
        </Link>
      </div>
    </TeacherShell>
  )
}

function formatPrompt(prompt) {
  return prompt.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
}
